import type {
  StyleSpecification,
  LayerSpecification,
  FilterSpecification,
} from "maplibre-gl";
import type { AtlasConfig } from "./atlas-config";

/** One style builder for both themes and every atlas lever. With the `v1` preset this
 *  emits exactly the layer set the old style-light/style-dark files did; atlas levers
 *  add layers (rivers, urban footprint, country/marine labels) or restyle the gazetteer.
 *  All atlas layers reference source-layers that may be absent from an older tileset —
 *  MapLibre renders nothing for a missing source-layer, so a stale world.pmtiles
 *  degrades to the v1 look instead of erroring. */

interface Palette {
  land: string;
  water: string;
  halo: string;
  states: string;
  borders: string;
  placeMinor: string;
  placeMid: string;
  placeMajor: string;
  /** Single muted voice for all gazetteer tiers when the `dim` lever is on. */
  placeDim: string;
  river: string;
  riverLabel: string;
  urban: string;
  country: string;
  marine: string;
}

const PALETTES: Record<"light" | "dark", Palette> = {
  light: {
    land: "#eef1f6",
    water: "#bccddd",
    halo: "#eef1f6",
    states: "#b8c4cc",
    borders: "#8fa0b0",
    placeMinor: "#607688",
    placeMid: "#45556a",
    placeMajor: "#2a3a48",
    placeDim: "#9aa8b5",
    river: "#aec3d6",
    riverLabel: "#7d95ab",
    urban: "#dbe2ee",
    country: "#68798b",
    marine: "#8ba1b5",
  },
  dark: {
    land: "#1e2a45",
    water: "#16213e",
    halo: "#1e2a45",
    states: "#2e3f60",
    borders: "#3a5080",
    placeMinor: "#7284a0",
    placeMid: "#9aabcc",
    placeMajor: "#d8e4fa",
    placeDim: "#566a8c",
    river: "#233457",
    riverLabel: "#5d7398",
    urban: "#263659",
    country: "#7e93b8",
    marine: "#4d6690",
  },
};

/** Add the atlas-mode suppression to a place-label filter: cities the marker layer
 *  names itself (concert flag baked into the tiles) must not also get a basemap label. */
function placeFilter(
  base: FilterSpecification,
  cfg: AtlasConfig
): FilterSpecification {
  if (cfg.markers.mode !== "atlas") return base;
  return ["all", base, ["!=", ["get", "concert"], 1]] as FilterSpecification;
}

export function buildStyle(
  theme: "light" | "dark",
  tilesUrl: string,
  cfg: AtlasConfig
): StyleSpecification {
  const p = PALETTES[theme];
  const bm = cfg.basemap;
  const layers: LayerSpecification[] = [];

  layers.push({
    id: "background",
    type: "background",
    paint: { "background-color": p.water },
  });
  layers.push({
    id: "land",
    type: "fill",
    source: "basemap",
    "source-layer": "land",
    paint: { "fill-color": p.land },
  });

  // The residual wash is inserted (at runtime, by markers.ts) between `land` and this
  // layer, so a field spilling a few px past the shore is overdrawn by a crisp water-
  // coloured shoreline instead of blurring the coast — the one thing that would make the
  // wash read as cheap dataviz rather than as part of the atlas. Only present when a
  // wash is actually on: on every other preset it would be an invisible extra draw.
  if (cfg.markers.residual === "wash" || cfg.markers.residual === "both") {
    layers.push({
      id: "coastline",
      type: "line",
      source: "basemap",
      "source-layer": "land",
      paint: {
        "line-color": p.water,
        "line-width": [
          "interpolate", ["linear"], ["zoom"],
          1, 0.6,
          6, 1.2,
        ],
      },
    });
  }

  if (bm.urban) {
    layers.push({
      id: "urban",
      type: "fill",
      source: "basemap",
      "source-layer": "urban",
      minzoom: 5.5,
      paint: {
        "fill-color": p.urban,
        // A whisper that strengthens as the reader closes in — at z9 the tint is what
        // shows where the city actually is on an otherwise empty stage.
        "fill-opacity": [
          "interpolate", ["linear"], ["zoom"],
          5.5, 0,
          6.5, Math.min(1, 0.35 * bm.urbanStrength),
          10, Math.min(1, 0.55 * bm.urbanStrength),
        ],
      },
    });
  }

  layers.push({
    id: "lakes",
    type: "fill",
    source: "basemap",
    "source-layer": "lakes",
    paint: { "fill-color": p.water },
  });

  if (bm.rivers) {
    layers.push({
      id: "rivers",
      type: "line",
      source: "basemap",
      "source-layer": "rivers",
      minzoom: 4,
      paint: {
        "line-color": p.river,
        "line-width": [
          "interpolate", ["linear"], ["zoom"],
          4, 0.3 * bm.riverStrength,
          7, 0.9 * bm.riverStrength,
          10, 2.4 * bm.riverStrength,
        ],
        "line-opacity": [
          "interpolate", ["linear"], ["zoom"],
          4, 0.35,
          6, Math.min(1, 0.8 * bm.riverStrength),
        ],
      },
    });
  }

  if (bm.states) {
    layers.push({
      id: "states",
      type: "line",
      source: "basemap",
      "source-layer": "states",
      minzoom: 3,
      paint: {
        "line-color": p.states,
        "line-width": ["interpolate", ["linear"], ["zoom"], 3, 0.4, 7, 0.9],
        "line-opacity": theme === "dark" ? 0.7 : 0.6,
        "line-dasharray": [4, 3],
      },
    });
  }

  layers.push({
    id: "borders",
    type: "line",
    source: "basemap",
    "source-layer": "borders",
    paint: {
      "line-color": p.borders,
      "line-width": ["interpolate", ["linear"], ["zoom"], 2, 0.6, 7, 1.2],
      "line-opacity": theme === "dark" ? 0.8 : 0.7,
    },
  });

  // ── Gazetteer (basemap place names) ─────────────────────────────
  // Least important first: MapLibre places symbols in reverse style order (topmost
  // claims collision space first), so major must come last of the three.
  if (bm.gazetteer !== "off") {
    const dim = bm.gazetteer === "dim";
    layers.push({
      id: "place-labels-minor",
      type: "symbol",
      source: "basemap",
      "source-layer": "places",
      minzoom: dim ? 6.5 : 6,
      filter: placeFilter([">=", ["get", "scalerank"], 6], cfg),
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Regular"],
        "text-size": dim
          ? ["interpolate", ["linear"], ["zoom"], 5, 9, 8, 10.5, 11, 13, 12, 15]
          : ["interpolate", ["linear"], ["zoom"], 5, 10, 8, 12, 11, 15, 12, 17],
        "text-anchor": "top",
        "text-offset": [0, 0.4],
        "text-max-width": 8,
        "symbol-sort-key": ["get", "scalerank"],
      },
      paint: {
        "text-color": dim ? p.placeDim : p.placeMinor,
        "text-halo-color": p.halo,
        "text-halo-width": 1.2,
      },
    });
    layers.push({
      id: "place-labels-mid",
      type: "symbol",
      source: "basemap",
      "source-layer": "places",
      minzoom: 4,
      filter: placeFilter(
        ["all", [">=", ["get", "scalerank"], 3], ["<=", ["get", "scalerank"], 5]],
        cfg
      ),
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Regular"],
        "text-size": dim
          ? ["interpolate", ["linear"], ["zoom"], 3, 10, 6, 11.5, 9, 14, 12, 16]
          : ["interpolate", ["linear"], ["zoom"], 3, 11, 6, 13, 9, 16, 12, 19],
        "text-anchor": "top",
        "text-offset": [0, 0.4],
        "text-max-width": 8,
        "symbol-sort-key": ["get", "scalerank"],
      },
      paint: {
        "text-color": dim ? p.placeDim : p.placeMid,
        "text-halo-color": p.halo,
        "text-halo-width": 1.5,
      },
    });
    layers.push({
      id: "place-labels-major",
      type: "symbol",
      source: "basemap",
      "source-layer": "places",
      minzoom: 2,
      filter: placeFilter(["<=", ["get", "scalerank"], 2], cfg),
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Medium"],
        "text-size": dim
          ? ["interpolate", ["linear"], ["zoom"], 2, 11, 5, 13, 9, 16, 12, 18]
          : ["interpolate", ["linear"], ["zoom"], 2, 12, 5, 15, 9, 19, 12, 22],
        "text-anchor": "top",
        "text-offset": [0, 0.4],
        "text-max-width": 8,
        "symbol-sort-key": ["get", "scalerank"],
      },
      paint: {
        "text-color": dim ? p.placeDim : p.placeMajor,
        "text-halo-color": p.halo,
        "text-halo-width": 1.6,
      },
    });
  }

  // ── River names: italic serif-adjacent voice along the line ─────
  if (bm.rivers) {
    layers.push({
      id: "river-labels",
      type: "symbol",
      source: "basemap",
      "source-layer": "rivers",
      minzoom: 7.5,
      filter: ["!=", ["get", "name"], ""],
      layout: {
        "symbol-placement": "line",
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Italic"],
        "text-size": ["interpolate", ["linear"], ["zoom"], 7.5, 10, 10, 13],
        "text-letter-spacing": 0.1,
      },
      paint: {
        "text-color": p.riverLabel,
        "text-halo-color": p.halo,
        "text-halo-width": 1.2,
      },
    });
  }

  // ── Atlas dressing: marine names, then country names on top ─────
  // Above the gazetteer so at globe zoom the atlas voice claims space first.
  if (bm.marineLabels) {
    layers.push({
      id: "marine-labels",
      type: "symbol",
      source: "basemap",
      "source-layer": "marine_labels",
      minzoom: 1,
      maxzoom: 7,
      filter: ["<=", ["get", "scalerank"], ["step", ["zoom"], 1, 3, 3, 5, 6]],
      layout: {
        "text-field": ["get", "name"],
        "text-font": ["Noto Sans Italic"],
        "text-size": [
          "interpolate", ["linear"], ["zoom"],
          1, ["-", 13, ["*", 1.2, ["get", "scalerank"]]],
          6, ["-", 19, ["*", 1.2, ["get", "scalerank"]]],
        ],
        "text-letter-spacing": 0.18,
        "text-max-width": 6,
      },
      paint: {
        "text-color": p.marine,
        "text-halo-color": p.water,
        "text-halo-width": 1,
      },
    });
  }

  if (bm.countryLabels) {
    layers.push({
      id: "country-labels",
      type: "symbol",
      source: "basemap",
      "source-layer": "country_labels",
      minzoom: 1.8,
      maxzoom: 6.5,
      // NE ships several label points per country (one per major landmass/exclave) with
      // scalerank marking importance; stage them in by zoom and never show the tiny
      // fragment ranks — z2 gets only the biggest countries, not seven Yemens.
      filter: ["<=", ["get", "scalerank"],
        ["step", ["zoom"], 1, 2.5, 3, 3.5, 5, 4.5, 6]],
      layout: {
        "text-field": ["upcase", ["get", "name"]],
        "text-font": ["Noto Sans Medium"],
        "text-size": [
          "interpolate", ["linear"], ["zoom"],
          2, 10,
          4, 13,
          6, 16,
        ],
        "text-letter-spacing": 0.3,
        "text-max-width": 7,
        "symbol-sort-key": ["get", "scalerank"],
      },
      paint: {
        "text-color": p.country,
        "text-opacity": [
          "interpolate", ["linear"], ["zoom"],
          1.8, 0.55,
          3, 0.85,
          5.5, 0.85,
          6.5, 0.4,
        ],
        "text-halo-color": p.halo,
        "text-halo-width": 1.3,
      },
    });
  }

  return {
    version: 8,
    name: `Classical Concert Map — ${theme === "dark" ? "Dark" : "Light"}`,
    // Globe at low zoom, mercator when zoomed in: one planet, nothing drawn twice.
    projection: { type: "globe" },
    sources: {
      basemap: { type: "vector", url: tilesUrl },
    },
    glyphs:
      "https://protomaps.github.io/basemaps-assets/fonts/{fontstack}/{range}.pbf",
    layers,
  };
}
